import { LogIn } from 'lucide-react';
import { ChangeEvent, useCallback, useEffect, useRef, useState } from 'react';

import { createProductReview } from '@screens/Product';
import { uploadReviewImages } from '@shared/api/uploadReviewImages/uploadReviewImages';
import { useAppDispatch, useAppSelector } from '@shared/hooks/redux';
import { HttpCodes } from '@shared/types/enums/http.enum';
import { ProductReviewsResponse } from '@shared/types/product';
import { Button } from '@shared/ui/Button';
import { Container } from '@shared/ui/Container';
import { Skeleton } from '@shared/ui/Skeleton';
import { VStack } from '@shared/ui/Stack';
import { showToast } from '@shared/utils/showToast/showToast';
import { openAlert } from '@widgets/AlertModal';
import { openAuthModal } from '@widgets/Auth';
import { refreshAccessToken } from '@widgets/Auth/api/refreshAccessToken/refreshAccessToken';

import { PublicationRulesModal } from './PublicationRulesModal';
import { ReviewForm } from './ReviewForm';
import { ReviewItem } from './ReviewItem';
import cls from './Reviews.module.scss';

const REVIEWS_STEP = 5;

type ReviewsProps = {
  productId: string;
  productReviews?: ProductReviewsResponse;
};

export const Reviews = ({ productId, productReviews }: ReviewsProps) => {
  const dispatch = useAppDispatch();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isAuthorized, setIsAuthorized] = useState<boolean | null>(null);
  const [selectedRate, setSelectedRate] = useState(0);
  const [hoveredRate, setHoveredRate] = useState(0);
  const [reviewText, setReviewText] = useState('');
  const [reviewFiles, setReviewFiles] = useState<File[]>([]);
  const [previewUrls, setPreviewUrls] = useState<string[]>([]);
  const [isSubmittingReview, setIsSubmittingReview] = useState(false);
  const [isPublicationRulesOpen, setIsPublicationRulesOpen] = useState(false);
  const [visibleReviewsCount, setVisibleReviewsCount] = useState(REVIEWS_STEP);

  const authModalIsOpen = useAppSelector((state) => state.auth.modalIsOpen);
  const reviews = productReviews?.reviews || [];
  const visibleReviews = reviews.slice(0, visibleReviewsCount);
  const hasReviews = reviews.length > 0;
  const canShowMoreButton = reviews.length > visibleReviewsCount;
  const isSubmitDisabled = isSubmittingReview || !selectedRate || !reviewText.trim();

  const handleClickPhotoButton = () => {
    fileInputRef.current?.click();
  };

  const handleClickLoginButton = useCallback(() => {
    dispatch(openAuthModal());
  }, [dispatch]);

  const handleOpenPublicationRules = useCallback(() => {
    setIsPublicationRulesOpen(true);
  }, []);

  const handleClosePublicationRules = useCallback(() => {
    setIsPublicationRulesOpen(false);
  }, []);

  const handleChangeFiles = (event: ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files || []);

    setReviewFiles((prevFiles) => [...prevFiles, ...files]);
    event.target.value = '';
  };

  const handleRemoveFile = useCallback((index: number) => {
    setReviewFiles((prevFiles) => prevFiles.filter((_, fileIndex) => fileIndex !== index));
  }, []);

  const handleShowMoreReviews = useCallback(() => {
    setVisibleReviewsCount((prevCount) => prevCount + REVIEWS_STEP);
  }, []);

  const handleSubmitReview = useCallback(async () => {
    const comment = reviewText.trim();

    if (!selectedRate || !comment) return;

    setIsSubmittingReview(true);

    const imageUrls = await uploadReviewImages(productId, reviewFiles).catch(() => null);

    if (!imageUrls) {
      setIsSubmittingReview(false);
      showToast('Не удалось загрузить фотографии');
      return;
    }

    const response = await createProductReview(productId, {
      rating: selectedRate,
      comment,
      imageUrls
    }).catch(() => null);

    setIsSubmittingReview(false);

    if (response?.code !== HttpCodes.CREATED) {
      dispatch(
        openAlert({
          data: {
            title: 'Не удалось отправить отзыв',
            text: response?.message || 'Попробуйте отправить отзыв позже'
          },
          variant: 'error'
        })
      );
      return;
    }

    setSelectedRate(0);
    setHoveredRate(0);
    setReviewText('');
    setReviewFiles([]);
    showToast('Отзыв отправлен на модерацию');
  }, [dispatch, productId, reviewFiles, reviewText, selectedRate]);

  useEffect(() => {
    const urls = reviewFiles.map((file) => URL.createObjectURL(file));

    setPreviewUrls(urls);

    return () => {
      urls.forEach((url) => URL.revokeObjectURL(url));
    };
  }, [reviewFiles]);

  useEffect(() => {
    let isMounted = true;

    const checkAuthorization = async () => {
      if (authModalIsOpen) return;

      const { ok } = await refreshAccessToken().catch(() => ({ ok: false }));

      if (isMounted) {
        setIsAuthorized(Boolean(ok));
      }
    };

    checkAuthorization();

    return () => {
      isMounted = false;
    };
  }, [authModalIsOpen]);

  return (
    <Container>
      <div className={cls.wrap}>
        <section className={isAuthorized ? cls.formSection : cls.authSection}>
          {isAuthorized === null ? (
            <div className={cls.authStatusSkeleton}>
              <Skeleton />
            </div>
          ) : (
            <>
              <h2 className={cls.title}>{isAuthorized ? 'Напишите свой отзыв' : 'Отзывы'}</h2>
              <p className={cls.description}>
                Ваш отзыв поможет нам стать лучше и поможет другим сделать правильный выбор
              </p>
            </>
          )}

          {isAuthorized === true && (
            <ReviewForm
              selectedRate={selectedRate}
              hoveredRate={hoveredRate}
              reviewText={reviewText}
              previewUrls={previewUrls}
              isSubmitDisabled={isSubmitDisabled}
              isSubmittingReview={isSubmittingReview}
              fileInputRef={fileInputRef}
              onChangeRate={setSelectedRate}
              onChangeHoveredRate={setHoveredRate}
              onChangeFiles={handleChangeFiles}
              onChangeReviewText={setReviewText}
              onClickPhotoButton={handleClickPhotoButton}
              onRemoveFile={handleRemoveFile}
              onSubmit={handleSubmitReview}
              onOpenPublicationRules={handleOpenPublicationRules}
            />
          )}

          {isAuthorized === false && (
            <div className={cls.authPrompt}>
              <LogIn
                className={cls.authIcon}
                width={16}
                height={16}
              />
              <div className={cls.authText}>
                Чтобы оставить отзыв,{' '}
                <button
                  className={cls.authButton}
                  type={'button'}
                  onClick={handleClickLoginButton}
                >
                  войдите
                </button>{' '}
                в личный кабинет
              </div>
            </div>
          )}
        </section>

        {hasReviews ? (
          <VStack className={cls.reviews}>
            {visibleReviews.map((review) => (
              <ReviewItem
                key={review.id}
                review={review}
              />
            ))}
          </VStack>
        ) : (
          <div className={cls.emptyState}>Отзывов пока нет</div>
        )}

        {canShowMoreButton && (
          <Button
            className={cls.moreButton}
            variant={'secondary'}
            size={'l'}
            onClick={handleShowMoreReviews}
          >
            Показать еще отзывы
          </Button>
        )}
      </div>

      {isPublicationRulesOpen && (
        <PublicationRulesModal
          isOpen={isPublicationRulesOpen}
          onClose={handleClosePublicationRules}
        />
      )}
    </Container>
  );
};
